import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/lib/api/client";
import { useAccount } from "@/features/account/hooks/useAccount";
import { assertStepUpProofWanted, type StepUpProof } from "@/features/account/api/stepup.api";
import {
  SUPPORT_CONTEXT_STORAGE_KEY,
  closeSupportContext,
  getSupportAccount,
  getSupportApps,
  getSupportDeployments,
  openSupportContext,
  searchAccounts,
  type StaffRole,
  type SupportContext,
} from "../api/staff.api";

export const staffKeys = {
  all: ["staff"] as const,
  account: (accountId: string, contextId: string) =>
    [...staffKeys.all, "account", accountId, contextId] as const,
  apps: (accountId: string, contextId: string) =>
    [...staffKeys.all, "apps", accountId, contextId] as const,
  deployments: (appId: string, contextId: string) =>
    [...staffKeys.all, "deployments", appId, contextId] as const,
};

export function useStaffRole(): { role: StaffRole | null; isLoading: boolean } {
  const { data, isLoading } = useAccount();
  const role = data?.staff?.role ?? null;
  return { role, isLoading };
}

function readStoredContext(): SupportContext | null {
  try {
    const raw = sessionStorage.getItem(SUPPORT_CONTEXT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SupportContext;
    return typeof parsed?.id === "string" && typeof parsed?.expiresAt === "string" ? parsed : null;
  } catch {
    return null;
  }
}

export function useSupportContext() {
  const queryClient = useQueryClient();
  const [context, setContext] = useState<SupportContext | null>(() => readStoredContext());
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    try {
      if (context) sessionStorage.setItem(SUPPORT_CONTEXT_STORAGE_KEY, JSON.stringify(context));
      else sessionStorage.removeItem(SUPPORT_CONTEXT_STORAGE_KEY);
    } catch {
      return;
    }
  }, [context]);

  useEffect(() => {
    if (!context || new Date(context.expiresAt).getTime() <= Date.now()) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [context, now]);

  const clear = useCallback(() => {
    setContext(null);
    queryClient.removeQueries({ queryKey: staffKeys.all });
  }, [queryClient]);
  const remainingMs = context ? Math.max(0, new Date(context.expiresAt).getTime() - now) : 0;

  return useMemo(
    () => ({
      context,
      setContext,
      clear,
      remainingMs,
      expired: context !== null && remainingMs <= 0,
    }),
    [context, clear, remainingMs],
  );
}

export function useAccountSearch() {
  return useMutation<Awaited<ReturnType<typeof searchAccounts>>, ApiError, string>({
    mutationFn: searchAccounts,
  });
}

export function useOpenSupportContext() {
  return useMutation<
    Awaited<ReturnType<typeof openSupportContext>>,
    ApiError,
    { targetAccountId: string; reason: string; stepUp?: StepUpProof }
  >({
    mutationFn: (vars) => {
      assertStepUpProofWanted(vars.stepUp);
      return openSupportContext(vars);
    },
  });
}

export function useCloseSupportContext() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: closeSupportContext,
    onSettled: () => {
      queryClient.removeQueries({ queryKey: staffKeys.all });
    },
  });
}

export function useSupportAccount(accountId: string | null, contextId: string | null) {
  return useQuery<Awaited<ReturnType<typeof getSupportAccount>>, ApiError>({
    queryKey: staffKeys.account(accountId ?? "", contextId ?? ""),
    queryFn: () => getSupportAccount(accountId!, contextId!),
    enabled: Boolean(accountId && contextId),
    retry: false,
  });
}

export function useSupportApps(accountId: string | null, contextId: string | null) {
  return useQuery<Awaited<ReturnType<typeof getSupportApps>>, ApiError>({
    queryKey: staffKeys.apps(accountId ?? "", contextId ?? ""),
    queryFn: () => getSupportApps(accountId!, contextId!),
    enabled: Boolean(accountId && contextId),
    retry: false,
  });
}

export function useSupportDeployments(appId: string | null, contextId: string | null) {
  return useQuery<Awaited<ReturnType<typeof getSupportDeployments>>, ApiError>({
    queryKey: staffKeys.deployments(appId ?? "", contextId ?? ""),
    queryFn: () => getSupportDeployments(appId!, contextId!),
    enabled: Boolean(appId && contextId),
    retry: false,
  });
}
